import httpClient from "../clients/http";

export interface PaymentMethod {
  provider: string;
  available: boolean;
  message: string;
}

export interface PaymentSession {
  SESSION: string;
  URL: string;
  success: boolean;
  message: string;
}

export interface PaymentButtonResponse {
  success: boolean;
  html: string;
  message: string;
}

export class PaymentSettingService {
  static async getPaymentMethodStatus(): Promise<{
    enabled: boolean;
    methods: string[];
  }> {
    const response = await httpClient.get("/settings/payments/status");
    return response.data;
  }
}

export class PaymentService {
  /**
   * Create a CardNet payment session for an order
   */
  static async createCardNetSession(orderId: string): Promise<PaymentSession> {
    const response = await httpClient.post(
      `/payment/cardnet/session/${orderId}`,
      {},
    );
    return response.data;
  }

  /**
   * Generate CardNet payment button HTML for an order
   */
  static async generateCardNetButton(
    orderId: string,
  ): Promise<PaymentButtonResponse> {
    const response = await httpClient.post(
      `/payment/cardnet/button/${orderId}`,
      {},
    );
    return response.data;
  }

  /**
   * Check CardNet configuration status
   */
  static async getCardNetStatus(): Promise<PaymentMethod> {
    const response = await httpClient.get("/payment/cardnet/status");
    return response.data;
  }

  /**
   * Redirect to CardNet payment page
   */
  static async redirectToCardNetPayment(orderId: string): Promise<void> {
    try {
      const session = await this.createCardNetSession(orderId);
      if (session.success) {
        // Create a form and submit it to redirect to CardNet
        const form = document.createElement("form");
        form.method = "POST";
        form.action = session.URL;
        form.style.display = "none";

        const sessionInput = document.createElement("input");
        sessionInput.type = "hidden";
        sessionInput.name = "SESSION";
        sessionInput.value = session.SESSION;

        form.appendChild(sessionInput);
        document.body.appendChild(form);
        form.submit();
      } else {
        throw new Error(session.message || "Failed to create payment session");
      }
    } catch (error) {
      console.error("CardNet payment error:", error);
      throw error;
    }
  }

  /**
   * Generate and inject CardNet payment button into a container
   */
  static async injectCardNetButton(
    orderId: string,
    containerId: string,
  ): Promise<void> {
    try {
      const buttonResponse = await this.generateCardNetButton(orderId);
      if (buttonResponse.success) {
        const container = document.getElementById(containerId);
        if (container) {
          container.innerHTML = buttonResponse.html;
        } else {
          throw new Error(`Container with id '${containerId}' not found`);
        }
      } else {
        throw new Error(
          buttonResponse.message || "Failed to generate payment button",
        );
      }
    } catch (error) {
      console.error("CardNet button generation error:", error);
      throw error;
    }
  }
}
